import { api } from './client';

export interface CodingCode {
  value: string; label: string; definition: string; dimension: string;
  parent?: string | null; inclusions: string; exclusions: string;
  examples: string[]; aliases: string[]; patterns: string[];
}
export interface Matrix {
  question: string; language: string; instructions: string; origin: string;
  normalizer: string;
  dimensions: {name: string; label: string; max_codes: number; required: boolean}[];
  codes: CodingCode[];
  states: {state: string; aliases: string[]}[];
}
export interface MatrixVersion {
  id: number; number: number; status: string; specification: Matrix; fingerprint: string;
}
export interface Codebook {
  id: number; name: string; description: string; versions: MatrixVersion[];
}
export interface CodingSurvey {
  id: number; project_id: number; form_id: string; enabled: boolean;
  automatic: boolean; interval_minutes: number; cases: number;
  synced_at: string | null; sync_error: string | null; capabilities: string[];
  dictionary: {name: string; label?: string; type?: string}[];
  options: Record<string, unknown>;
  bindings: {id: number; variable: string; output_name: string; version_id: number; review_codes?: Pick<CodingCode,'value'|'label'|'definition'|'dimension'>[]}[];
}
export const CodingAPI = {
  surveys: (pid: number, fid: string) => api.get<CodingSurvey[]>(`/api/coding/surveys?project_id=${pid}&form_id=${encodeURIComponent(fid)}`),
  createSurvey: (project_id: number, form_id: string) => api.post<CodingSurvey>('/api/coding/surveys', {project_id, form_id}),
  configure: (id: number, body: unknown) => api.patch<CodingSurvey>(`/api/coding/surveys/${id}`, body),
  books: (pid: number) => api.get<Codebook[]>(`/api/coding/codebooks?project_id=${pid}`),
  createBook: (project_id: number, name: string, specification: Matrix) => api.post('/api/coding/codebooks', {project_id, name, specification}),
  newVersion: (id: number, spec: Matrix) => api.post(`/api/coding/codebooks/${id}/versions`, spec),
  publish: (id: number) => api.post(`/api/coding/versions/${id}/publish`),
  exportMatrix: (id: number) => api.get(`/api/coding/versions/${id}/export`),
  bind: (id: number, variable: string, version_id: number, output_name: string) => api.post(`/api/coding/surveys/${id}/bindings`, {variable, version_id, output_name}),
  unbind: (surveyId: number, bindingId: number) => api.del<{ ok: boolean }>(`/api/coding/surveys/${surveyId}/bindings/${bindingId}`),
  sync: (id: number) => api.post<{ new: number; changed: number; removed: number; unchanged: number }>(`/api/coding/surveys/${id}/sync`),
  sampleResponses: (surveyId: number, variable: string, limit: number = 25) =>
    api.get<{ total: number; items: { case_id: number; source_id: string; text: string }[] }>(
      `/api/coding/surveys/${surveyId}/sample-responses?variable=${encodeURIComponent(variable)}&limit=${limit}`
    ),
  getMatrixPrompt: (projectId?: number) =>
    api.get<MatrixPromptInfo>(`/api/coding/matrix-prompt${projectId ? `?project_id=${projectId}` : ''}`),
  proposeMatrix: (payload: ProposeMatrixPayload) =>
    api.post<ProposedMatrixResponse>('/api/coding/propose-matrix', payload),
};

export interface MatrixPromptInfo {
  clave: string;
  titulo: string;
  descripcion: string;
  variables: string[];
  default: { sistema: string; usuario: string };
  vigente: { sistema: string; usuario: string };
  esquema_ejemplo: string;
  ia_configurada: boolean;
  proveedor: string;
  modelo: string;
}

export interface ProposeMatrixPayload {
  project_id: number;
  question: string;
  sample_responses?: string[];
  instructions?: string;
  n_categories?: number;
  custom_system_prompt?: string;
  custom_user_prompt?: string;
  survey_id?: number;
  variable_name?: string;
}

export interface ProposedMatrixResponse {
  name: string;
  description: string;
  specification: Matrix;
  usage: {
    provider: string;
    model: string;
    input_tokens: number;
    output_tokens: number;
    cost: number;
    latency_ms: number;
    retried_without_reasoning: boolean;
  };
}

export interface CodingRun {
  id: string; status: string; mode: string; total: number; finished: number; progress: number;
  counts: Record<string,number>; summary: Record<string,number>; errors?: {task_id:number;message:string}[];
  created_at?: string; completed_at?: string;
}
export interface CodingDecision {
  id:number;case_id:number;source_id:string;text:string;response_state:string;review_state:string;
  codes:string[];method:string;protected:boolean;evidence:Record<string,string>;
}
export interface CodingRelease {id:number;name:string;cases:number;missing:number;unreviewed:number;created_at:string;}
export interface CodingFrequency {
  n:number;n_base:number;states:Record<string,number>;coverage:number;warning:string;
  categories:{code:string;label:string;dimension:string;n:number;percent:number|null;denominator:number}[];
}
export const CodingWorkAPI = {
  estimate:(id:number,body:unknown)=>api.post<{summary:Record<string,number>;tasks:number;possible_ai_calls:number;estimated_cost:number;warning:string}>(`/api/coding/surveys/${id}/estimate`,body),
  start:(id:number,body:unknown)=>api.post<CodingRun>(`/api/coding/surveys/${id}/runs`,body),
  runs:(id:number)=>api.get<CodingRun[]>(`/api/coding/surveys/${id}/runs`),
  run:(id:string)=>api.get<CodingRun>(`/api/coding/runs/${id}`),
  control:(id:string,action:string)=>api.post<CodingRun>(`/api/coding/runs/${id}/${action}`),
  cancelActive:(id:number)=>api.post<{status:string;run?:CodingRun}>(`/api/coding/surveys/${id}/runs/cancel-active`),
  decisions:(id:number,state:string,offset:number)=>api.get<{total:number;items:CodingDecision[]}>(`/api/coding/bindings/${id}/decisions?limit=25&offset=${offset}${state?'&review_state='+state:''}`),
  review:(id:number,body:unknown)=>api.post(`/api/coding/decisions/${id}/review`,body),
  releases:(id:number)=>api.get<CodingRelease[]>(`/api/coding/surveys/${id}/releases`),
  publish:(id:number,body:unknown)=>api.post<CodingRelease>(`/api/coding/surveys/${id}/releases`,body),
  frequencies:(id:number,body:unknown)=>api.post<CodingFrequency>(`/api/coding/releases/${id}/frequencies`,body),
  activate:(id:number)=>api.post(`/api/coding/releases/${id}/activate`),
  original:(id:number)=>api.post(`/api/coding/surveys/${id}/original-analysis`),
  export:(id:number,format:string)=>api.getBlob(`/api/coding/releases/${id}/export?format=${format}`),
};
